import {
  Box,
  CloseButton,
  Dialog,
  Flex,
  Heading,
  Portal,
  Text,
  VStack,
} from "@chakra-ui/react";
import { DataTable, StatusBadge, palette } from "./ui";
import { money } from "@/hooks/useNumberFormatter";

export type SettlementRow = {
  customer: string;
  contract: string;
  amount: number;
  paymentDate: string | null;
  settlementDate: string;
  bucket: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  nextSettlementDate: string | null;
  nextAmount: number;
  nextInvoices: number;
  laterAmount: number;
  laterInvoices: number;
  rows: SettlementRow[];
};

/**
 * Invoice-level detail behind "Dinero por recibir": which already-paid invoices
 * land in the imminent deposit and which slip to the next cycle. Every row here
 * is money the tenant has already handed over, so nothing in this modal is a
 * collections task.
 */
const SettlementDrilldownModal = ({
  open,
  onClose,
  nextSettlementDate,
  nextAmount,
  nextInvoices,
  laterAmount,
  laterInvoices,
  rows,
}: Props): JSX.Element => (
  <Dialog.Root open={open} onOpenChange={(e) => !e.open && onClose()} size="lg">
    <Portal>
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content
          bg="white"
          borderRadius="12px"
          maxW="780px"
          maxH="90vh"
          overflow="auto"
        >
          <Dialog.Header borderBottom="1px solid #EBECEF" px="6" py="4">
            <Flex justify="space-between" align="center" w="100%" gap={4}>
              <Box>
                <Dialog.Title>
                  <Heading size="md" color={palette.text}>
                    Detalle del abono
                  </Heading>
                </Dialog.Title>
                <Text color={palette.muted} fontSize="13px" mt="1">
                  {nextSettlementDate
                    ? `Proximo abono el ${nextSettlementDate} · ${nextInvoices} facturas`
                    : "Sin abonos pendientes"}
                </Text>
              </Box>
              <Flex gap={4}>
                <Box textAlign="center">
                  <Text fontSize="20px" fontWeight="800" color={palette.green}>
                    {money(nextAmount)}
                  </Text>
                  <Text fontSize="11px" color={palette.muted}>
                    Proximo abono
                  </Text>
                </Box>
                <Box textAlign="center">
                  <Text fontSize="20px" fontWeight="800" color={palette.yellow}>
                    {money(laterAmount)}
                  </Text>
                  <Text fontSize="11px" color={palette.muted}>
                    Siguiente ciclo · {laterInvoices}
                  </Text>
                </Box>
              </Flex>
            </Flex>
          </Dialog.Header>
          <Dialog.CloseTrigger asChild>
            <CloseButton
              position="absolute"
              top="4"
              right="4"
              onClick={onClose}
            />
          </Dialog.CloseTrigger>
          <Dialog.Body px="6" py="5">
            <VStack gap={4} align="stretch">
              {rows.length === 0 ? (
                <Text color={palette.muted} fontSize="13px">
                  No hay pagos en transito en este momento.
                </Text>
              ) : (
                <DataTable
                  headers={[
                    "Inquilino",
                    "Contrato",
                    "Monto",
                    "Pagado",
                    "Abono",
                    "Ciclo",
                  ]}
                  rows={rows.map((row, index) => [
                    <Text
                      key={`${row.contract}-${index}-name`}
                      fontWeight="600"
                      fontSize="12px"
                      color={palette.text}
                    >
                      {row.customer || "Sin nombre"}
                    </Text>,
                    row.contract,
                    <Text
                      key={`${row.contract}-${index}-amt`}
                      fontWeight="700"
                      fontSize="12px"
                    >
                      {money(row.amount)}
                    </Text>,
                    row.paymentDate ?? "—",
                    row.settlementDate,
                    <StatusBadge
                      key={`${row.contract}-${index}-bucket`}
                      color={row.bucket === "next" ? "green" : "yellow"}
                    >
                      {row.bucket === "next" ? "Proximo" : "Siguiente"}
                    </StatusBadge>,
                  ])}
                />
              )}
            </VStack>
          </Dialog.Body>
        </Dialog.Content>
      </Dialog.Positioner>
    </Portal>
  </Dialog.Root>
);

export default SettlementDrilldownModal;
